import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import type { TargetareJob, TargetareSnapshot } from "@/types/targetare";
import { formatDataOra } from "@/utils/targetareFormatting";

type SurseDateProps = {
  snapshoturi: TargetareSnapshot[];
  joburi: TargetareJob[];
};

const SURSE_LABELS: Record<string, string> = {
  onrc: "ONRC · exportul data.gov.ro „Firme înregistrate la Registrul Comerțului până la data de 02.09.2026” (firme, coduri CAEN autorizate, stări, reprezentanți)",
  mf_bilant: "Ministerul Finanțelor · situații financiare",
  nomenclator: "ONRC · nomenclatoare",
};

const STARE_JOB_VARIANT: Record<
  TargetareJob["stare"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  programat: "outline",
  in_curs: "default",
  finalizat: "secondary",
  esuat: "destructive",
};

export function SurseDate({ snapshoturi, joburi }: SurseDateProps) {
  return (
    <Collapsible className="rounded-lg border bg-card">
      <CollapsibleTrigger className="flex w-full items-center justify-between p-4 text-left text-sm font-heading font-semibold">
        Surse de date și rulări
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 px-4 pb-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Snapshot-uri importate
          </p>
          <ul className="mt-1 space-y-1 text-sm">
            {snapshoturi.map((s, i) => (
              <li key={`${s.sursa}-${i}`} className="flex flex-wrap gap-x-2">
                <span className="font-medium">
                  {SURSE_LABELS[s.sursa] ?? s.sursa}
                </span>
                <span className="text-muted-foreground">
                  publicat {s.data_publicarii ?? "–"} · importat{" "}
                  {formatDataOra(s.importat_la)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Ultimele rulări
          </p>
          {joburi.length === 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Nicio rulare înregistrată.
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {joburi.map((j) => (
                <li key={j.id} className="flex flex-wrap items-center gap-2">
                  <Badge variant={STARE_JOB_VARIANT[j.stare]}>{j.stare}</Badge>
                  <span className="font-medium">{j.tip}</span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {formatDataOra(j.inceput_la)}
                  </span>
                  {j.jurnal && (
                    <span className="text-muted-foreground">{j.jurnal}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
